import React, { useCallback } from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import * as styles from "./footer.module.css"
import logoMkbsd from "../../images/mkbsd26-logo.svg"
import FooterNewsletter from "../footer-newsletter/footer-newsletter"
import { trackEvent } from "../../modules/analytics"
import linkBuilder from "../../modules/linkBuilder"

const footerColumnDefinitions = [
  {
    title: "Snel naar",
    source: "heroMenu",
    limit: 8,
    mapItem: (item) => ({
      label: item.name,
      href: linkBuilder.desk(item),
    }),
  },
  {
    title: "Trending whitepapers",
    source: "whitepapers",
    limit: 6,
    mapItem: (item) => ({
      label: item.title,
      href: linkBuilder.download(item),
    }),
  },
  {
    title: "Verbeter jezelf met onze tools",
    source: "tools",
    limit: 6,
    mapItem: (item) => ({
      label: item.title,
      href: linkBuilder.tool(item),
    }),
  },
]

const buildFooterColumns = (page) =>
  footerColumnDefinitions
    .map((column) => {
      const items = (page?.[column.source] ?? [])
        .slice(0, column.limit)
        .map(column.mapItem)
        .filter((item) => item.label && item.href && item.href !== "/undefined")

      return {
        title: column.title,
        links: items,
      }
    })
    .filter((column) => column.links.length > 0)

const socials = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/220526",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5.001zM3 9.5h4V21H3V9.5zM9.5 9.5h3.83v1.57h.05c.53-1 1.84-2.06 3.78-2.06 4.05 0 4.8 2.66 4.8 6.13V21h-4v-5.05c0-1.2-.02-2.74-1.67-2.74-1.67 0-1.93 1.31-1.93 2.66V21h-4V9.5z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/mkbservicedesk",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/MKBServicedesk",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 21v-8h2.7l.4-3.2h-3.1V7.7c0-.9.3-1.6 1.6-1.6h1.7V3.2c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H7.4V13h2.7v8h3.4z" />
      </svg>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/user/MKBservicedesk",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M22.54 7.2a2.78 2.78 0 0 0-1.96-1.96C18.84 4.75 12 4.75 12 4.75s-6.84 0-8.58.49A2.78 2.78 0 0 0 1.46 7.2 29 29 0 0 0 1 12a29 29 0 0 0 .46 4.8 2.78 2.78 0 0 0 1.96 1.96c1.74.49 8.58.49 8.58.49s6.84 0 8.58-.49a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-4.8zM9.9 15.5v-7l5.85 3.5L9.9 15.5z" />
      </svg>
    ),
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@mkbservicedesk",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.6 7.5a5.6 5.6 0 0 1-3.4-1.13 5.62 5.62 0 0 1-2.18-3.62h-3.2v12.42c0 1.5-1.22 2.72-2.72 2.72a2.72 2.72 0 0 1 0-5.44c.28 0 .55.04.81.12V9.3a5.95 5.95 0 0 0-.81-.06 5.93 5.93 0 1 0 5.93 5.93V9.42a8.78 8.78 0 0 0 5.57 1.95V8.18c-.01 0 0-.68 0-.68z" />
      </svg>
    ),
  },
    {
    label: "X",
    href: "https://x.com/MKBServicedesk",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.3,21.2l-7.6-10.8l6.5-7.5h-1.8l-5.5,6.4L8.5,2.9H2.7L9.7,13l-7.1,8.2h1.9l6-7l4.9,7H21.3z M7.8,4.5l10.7,15.2h-2.3L5.5,4.5H7.8z"/>
      </svg>
    ),
  },
]

const legalLinks = [
  { label: "Algemene voorwaarden", href: "/algemene-voorwaarden" },
  { label: "Privacy statement", href: "/privacy-verklaring" },
]

const FooterLink = ({ href, children, className, onClick }) => {
  const isInternal = href.startsWith("/")
  if (isInternal) {
    return (
      <Link className={className} to={href} onClick={onClick}>
        {children}
      </Link>
    )
  }
  return (
    <a
      className={className}
      href={href}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      target={href.startsWith("http") ? "_blank" : undefined}
      onClick={onClick}
    >
      {children}
    </a>
  )
}

const Footer = () => {
  const data = useStaticQuery(graphql`
    query {
      contentfulPage(slug: { eq: "index" }) {
        heroMenu {
          ... on ContentfulDesk {
            name
            slug
            slug2
          }
        }
        whitepapers {
          title
          slug
          downloadType
        }
        tools {
          title
          slug
        }
      }
    }
  `)

  const handleFooterClickTracking = useCallback((eventName, href) => {
    trackEvent(eventName, { link_url: href })
  }, [])

  const handleCookiePrefs = useCallback(() => {
    trackEvent("nav_footer_cookie_settings")
    const _hsp = (window._hsp = window._hsp || [])
    _hsp.push(["showBanner"])
  }, [])

  const footerColumns = buildFooterColumns(data.contentfulPage)

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={styles.wrap}>
        {/* ── Top section ── */}
        <div className={styles.top}>
          {/* Column 1: Brand + newsletter */}
          <div className={styles.brand}>
            <Link to="/" aria-label="MKB Servicedesk home">
              <img
                className={styles.brandLogo}
                src={logoMkbsd}
                alt="MKB Servicedesk"
              />
            </Link>

            <FooterNewsletter />
          </div>

          {/* Column 2: Link columns */}
          <div className={styles.links}>
            {footerColumns.map((col, colIndex) => {
              const headingId = `footer-links-heading-${colIndex + 1}`

              return (
                <nav className={styles.linksCol} key={col.title} aria-labelledby={headingId}>
                  <h3 id={headingId} className={styles.linksColTitle}>{col.title}</h3>
                  <ul className={styles.linksColList}>
                    {col.links.map((link) => (
                      <li key={link.href}>
                        <FooterLink
                          className={styles.linksColLink}
                          href={link.href}
                          onClick={() =>
                            handleFooterClickTracking("nav_footer_link", link.href)
                          }
                        >
                          {link.label}
                        </FooterLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              )
            })}
          </div>

          {/* Column 3: Contact card */}
          <aside
            className={styles.contact}
            aria-labelledby="footer-contact-heading"
          >
            <p className={styles.contactBrand}>MKB Servicedesk</p>
            <h3 id="footer-contact-heading" className={styles.contactTitle}>
              Contact
            </h3>
            <address className={styles.contactLines}>
              Reitseplein 1<br />
              5037 AA Tilburg<br />
              <a href="tel:+31135944445">013‑5944445</a>
            </address>
            <FooterLink
              className={styles.contactCta}
              href="/nieuws/mkb-servicedesk/contact-mkb-servicedesk"
              onClick={() =>
                handleFooterClickTracking("cta_footer_contact", "/nieuws/mkb-servicedesk/contact-mkb-servicedesk")
              }
            >
              <span className={styles.contactCtaContent}>
                <span className={styles.contactCtaText}>Neem contact op</span>
              </span>
            </FooterLink>
          </aside>
        </div>

        {/* ── Bottom section ── */}
        <div className={styles.bottom}>
          <div className={styles.socials} role="navigation" aria-label="Volg ons">
            {socials.map((s) => (
              <a
                key={s.label}
                className={styles.social}
                href={s.href}
                aria-label={"Volg ons op " + s.label}
                rel="noopener noreferrer"
                target="_blank"
                onClick={() =>
                  handleFooterClickTracking("nav_footer_social", s.href)
                }
              >
                {s.icon}
              </a>
            ))}
          </div>

          <div className={styles.legal}>
            {legalLinks.map((link) => (
              <FooterLink
                key={link.href}
                className={styles.legalLink}
                href={link.href}
                onClick={() =>
                  handleFooterClickTracking("nav_footer_legal", link.href)
                }
              >
                {link.label}
              </FooterLink>
            ))}
            <button
              type="button"
              id="hs_show_banner_button"
              className={styles.legalLink}
              onClick={handleCookiePrefs}
            >
              Cookie instellingen
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer